import { Archive, Copy, RefreshCw, Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, DiscordRole, GuildConfig, ServerBackup } from "@/lib/api";
import { Section, Field } from "@/components/panel/Section";
import { MultiSelect } from "@/components/MultiSelect";

interface Props {
  config: GuildConfig;
  roles: DiscordRole[];
  onChange: (v: GuildConfig["backup"]) => void;
}

function fmtSize(bytes: number): string {
  if (!bytes || bytes < 1024) return `${bytes || 0} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function fmtDate(ts: number | null): string {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return "—";
  }
}

export function BackupList({ guildId }: { guildId: string }) {
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["server-backups", guildId],
    queryFn: () => api.serverBackups(guildId),
    enabled: !!guildId,
  });

  const backups: ServerBackup[] = data?.backups ?? [];

  const copyId = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
      toast.success("Backup ID copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium">
          Saved backups {backups.length ? `(${backups.length})` : ""}
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-1.5 rounded-md border border-border/80 bg-muted/30 px-2.5 py-1 text-xs text-muted-foreground hover:bg-muted/60 disabled:opacity-60"
        >
          {isFetching ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
          Refresh
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 rounded-lg border border-border/60 bg-muted/10 p-4 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading backups…
        </div>
      ) : isError ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          Couldn't load backups
          {error instanceof Error ? `: ${error.message}` : "."}
        </div>
      ) : backups.length === 0 ? (
        <div className="rounded-lg border border-border/60 bg-muted/10 p-4 text-sm text-muted-foreground">
          No backups for this server yet. Create one with <strong>/backup create</strong>{" "}
          in Discord — it'll show up here afterwards.
        </div>
      ) : (
        <ul className="space-y-2">
          {backups.map((b) => (
            <li
              key={b.id}
              className="flex items-center gap-3 rounded-lg border border-border/80 bg-muted/20 p-3"
            >
              {b.icon_url ? (
                <img
                  src={b.icon_url}
                  alt=""
                  className="h-9 w-9 shrink-0 rounded-full object-cover"
                />
              ) : (
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                  <Archive className="h-4 w-4" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">
                  {b.name || "Server backup"}
                </div>
                <div className="truncate text-xs text-muted-foreground">
                  {fmtDate(b.created_at)} · {fmtSize(b.size_bytes)}
                </div>
                <code className="mt-0.5 block truncate text-[11px] text-muted-foreground/80">
                  {b.id}
                </code>
              </div>
              <button
                type="button"
                onClick={() => copyId(b.id)}
                title="Copy backup ID"
                className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border/80 bg-background px-2.5 py-1.5 text-xs hover:bg-muted/60"
              >
                <Copy className="h-3.5 w-3.5" /> ID
              </button>
            </li>
          ))}
        </ul>
      )}
      <p className="text-xs text-muted-foreground">
        Restore into the server: <strong>/backup import id:&lt;ID&gt;</strong> in Discord.
      </p>
    </div>
  );
}

export function BackupTab({ config, roles, onChange }: Props) {
  const cfg = config.backup;
  const update = <K extends keyof typeof cfg>(k: K, v: (typeof cfg)[K]) =>
    onChange({ ...cfg, [k]: v });

  return (
    <Section
      icon={Archive}
      title="Discord server backup"
      description="**Areon — Backup:** **/backup create** saves the current server structure and gives you a backup ID. **/backup list** shows your saved IDs. **/backup import id:<id>** restores that backup (clears the server first — use carefully)."
      enabled={cfg.enabled}
      onToggle={(v) => update("enabled", v)}
    >
      <div className="rounded-lg border border-border/80 bg-muted/20 p-3 text-sm text-muted-foreground space-y-2">
        <p>
          <strong>/backup create</strong> — set a <strong>label</strong> and optionally{" "}
          <code>include_bot_settings</code> for the web panel. The reply lists your label, the
          <strong>source server name at save time</strong>, and stores JSON under your user (not
          this server id).
        </p>
        <p>
          <strong>/backup import</strong> — <code>confirm</code> and usually <code>wipe: true</code>{" "}
          (Orbit: clear removable channels/roles, then re-create to match the snapshot). Use
          the <code>name_or_file</code> with just your <strong>label</strong>, a substring, or
          <code>backup_….json</code>, or upload an <code>attachment</code>. Put the bot’s role
          <strong>at the top</strong> of the server role list.
        </p>
        <p>
          <strong>/backup load</strong> only applies the JSON <code>config</code> block to this
          server (panel) when you choose <strong>Apply web / panel</strong>. Full import / merge
          are available from the same menu for recreating channels and roles.
        </p>
        <p>
          The backup label and file on create are visible only to you (ephemeral) and
          are not sent to public channels.
        </p>
      </div>

      <Field
        label="Who may run create and view the list (create / list)"
        hint="If left empty, anyone with Manage Server or Administrator may run /backup create and /backup list."
      >
        <MultiSelect
          options={roles}
          value={cfg.create_role_ids}
          onChange={(v) => update("create_role_ids", v)}
          prefix="@"
          placeholder="+ specific roles (e.g. Team)"
        />
      </Field>
    </Section>
  );
}
